/**
 * Was eine Morgenmeditation mit ElevenLabs v4 kostet — der Satz in der Karte
 * „ElevenLabs v4 für die Meditation". (07.10.2026)
 *
 * EINE Stelle für den Preisstand dieser Karte. Die Sätze je 1.000 Zeichen
 * stehen schon in shared/dienst-preise.ts (ZEICHEN_PREISE für ElevenLabs,
 * SONIOX_TTS für Soniox) und werden von dort gerechnet; hier steht nur, was
 * die Karte zusätzlich braucht: die übliche Länge, die Aktion und das Datum
 * der Preisseite. Ändert sich ein Satz, ändert er sich dort; endet die Aktion,
 * fällt ihr Teil am Tag danach von selbst aus dem Satz.
 *
 * Preisseite: https://elevenlabs.io/pricing/api, abgerufen am 07.10.2026:
 * v4 „$0.08 Price per 1K characters", Aktion „72% off until Oct 12" ($0.022).
 */
import { kostenSprachausgabe } from "./dienst-preise.js";

export const MEDITATION_PREISSTAND = {
  /** Wann die Preisseite gelesen wurde. */
  abgerufen: "2026-10-07",
  /** Das Modell der Meditation (= MEDITATION_ELEVEN_MODELL, server/eleven-v4.ts). */
  modell: "eleven_v4",
  /** Eine übliche Meditation, in Zeichen (Ziel 5.500 bis 7.000, server/morgen-meditation.ts). */
  zeichen: 5600,
  /** ElevenLabs zieht je gesprochenem Zeichen einen Credit ab. */
  creditsJeZeichen: 1,
  /** Die Aktion auf der Preisseite: v4 je 1.000 Zeichen, gültig bis einschließlich. */
  aktion: { usdJeTausend: 0.022, bis: "2026-10-12" },
  /** Soniox spricht etwa so schnell (Konrad im Normaltempo, gemessen 24.09., shared/meditations-stimme.ts). */
  sonioxZeichenJeSekunde: 12.7,
} as const;

interface MeditationKosten {
  credits: number;
  usd: number;
  /** Der Aktionspreis — nur, solange die Aktion läuft. */
  aktion: { usd: number; bis: string } | null;
  sonioxUsd: number;
}

/** Die Kosten einer üblichen Meditation, gerechnet aus den Sätzen in dienst-preise.ts. */
export function meditationKosten(heute: Date = new Date()): MeditationKosten {
  const p = MEDITATION_PREISSTAND;
  const tag = heute.toISOString().slice(0, 10);
  return {
    credits: p.zeichen * p.creditsJeZeichen,
    usd: kostenSprachausgabe({ anbieter: "elevenlabs", modell: p.modell, zeichen: p.zeichen }).usd,
    aktion: tag <= p.aktion.bis ? { usd: (p.zeichen / 1000) * p.aktion.usdJeTausend, bis: p.aktion.bis } : null,
    sonioxUsd: kostenSprachausgabe({
      anbieter: "soniox",
      zeichen: p.zeichen,
      sekunden: p.zeichen / p.sonioxZeichenJeSekunde,
    }).usd,
  };
}

const zahl = (n: number, stellen: number): string =>
  n.toLocaleString("de-DE", { minimumFractionDigits: stellen, maximumFractionDigits: stellen });

/** „12.10." aus „2026-10-12". */
const tagMonat = (iso: string): string => `${Number(iso.slice(8, 10))}.${Number(iso.slice(5, 7))}.`;

/** Der Satz der Karte — in Worten, deutsche Schreibweise. */
export function meditationKostenSatz(heute: Date = new Date()): string {
  const k = meditationKosten(heute);
  const aktion = k.aktion
    ? ` (bis ${tagMonat(k.aktion.bis)} in der Aktion etwa ${zahl(k.aktion.usd, 2)} Dollar)`
    : "";
  return (
    `Kostet ElevenLabs-Credits statt Soniox: etwa ${zahl(k.credits, 0)} Credits je Meditation, ` +
    `das sind etwa ${zahl(k.usd, 2)} Dollar${aktion}. Mit Soniox kostet eine Meditation etwa ` +
    `${zahl(k.sonioxUsd, 2)} Dollar.`
  );
}
